/* eslint-disable @typescript-eslint/no-explicit-any */ import { nanoid } from "nanoid";
import { createRecordType, defineMigrations } from "@supersig/store";
import { T } from "@supersig/validate";
import { mapObjectMapValues } from "../../utils";
import { createNodeValidator } from "../nodes/SWBaseNode";
import { createStateId } from "./SWState";
/** @internal */ export const rootNodeVersions = {};
/** @internal */ export const rootNodeMigrations = defineMigrations({});
/** @public */ export function isNode(record) {
    if (!record) return false;
    return record.typeName === "node";
}
/** @public */ export function isNodeId(id) {
    if (!id) return false;
    return id.startsWith("node:");
}
/** @public */ export function createNodeId(id) {
    return `node:${id ?? nanoid()}`;
}
export function castNodeId(id) {
    return {
        toStateId (propertyName) {
            const rawNodeId = id.replace("node:", "");
            return createStateId(`node.${rawNodeId}.${propertyName}`);
        }
    };
}
/** @internal */ export const nodeVersions = {
    AddRenderingHelperToPropsImagePropsVideoPropsLottie: 1
};
/** @internal */ export function createNodeRecordType(nodes) {
    return createRecordType("node", {
        migrations: defineMigrations({
            currentVersion: nodeVersions.AddRenderingHelperToPropsImagePropsVideoPropsLottie,
            migrators: {
                [nodeVersions.AddRenderingHelperToPropsImagePropsVideoPropsLottie]: {
                    up: (record)=>{
                        if (record.type === "img" || record.type === "video" || record.type === "lottie") {
                            // Create copies of the default properties and properties
                            const updatedDefaultProperties = {
                                ...record.defaultProperties
                            };
                            const updatedProperties = {
                                ...record.properties
                            };
                            // Get the current property key for the node type
                            const propKey = record.type === "img" ? "prop:image" : record.type === "video" ? "prop:video" : "prop:lottie";
                            // Function to update the rendering field
                            const updateRendering = (prop)=>{
                                if (prop?.type === "literal") {
                                    const propValue = prop.value;
                                    if (propValue && !propValue.rendering) {
                                        // Add a rendering field to the literal
                                        propValue.rendering = {
                                            type: "literal"
                                        };
                                    }
                                }
                            };
                            // Update defaultProperties
                            updateRendering(updatedDefaultProperties[propKey]);
                            // Only update properties if the specific property exists
                            if (updatedProperties[propKey]) {
                                updateRendering(updatedProperties[propKey]);
                            }
                            return {
                                ...record,
                                defaultProperties: updatedDefaultProperties,
                                properties: updatedProperties
                            };
                        }
                        return record;
                    },
                    down: (record)=>{
                        if (record.type === "img" || record.type === "video" || record.type === "lottie") {
                            // Create copies of the default properties and properties
                            const downgradedDefaultProperties = {
                                ...record.defaultProperties
                            };
                            const downgradedProperties = {
                                ...record.properties
                            };
                            // Get the current property key for the node type
                            const propKey = record.type === "img" ? "prop:image" : record.type === "video" ? "prop:video" : "prop:lottie";
                            // Function to remove the rendering field
                            const removeRendering = (prop)=>{
                                if (prop?.type === "literal") {
                                    const propValue = prop.value;
                                    if (propValue && propValue.rendering) {
                                        // Remove the rendering field
                                        delete propValue.rendering;
                                    }
                                }
                            };
                            // Update defaultProperties
                            removeRendering(downgradedDefaultProperties[propKey]);
                            // Only update properties if the specific property exists
                            if (downgradedProperties[propKey]) {
                                removeRendering(downgradedProperties[propKey]);
                            }
                            return {
                                ...record,
                                defaultProperties: downgradedDefaultProperties,
                                properties: downgradedProperties
                            };
                        }
                        return record;
                    }
                }
            },
            subTypeKey: "type",
            subTypeMigrations: mapObjectMapValues(nodes, (_, v)=>v.migrations ?? defineMigrations({}))
        }),
        scope: "document",
        validator: T.model("node", T.union("type", mapObjectMapValues(nodes, (type, { props, meta })=>createNodeValidator(type, props, meta))))
    }).withDefaultProperties(()=>({
            x: 0,
            y: 0,
            rotation: 0,
            isLocked: false,
            opacity: 1,
            defaultProperties: {},
            properties: {},
            meta: {},
            requiredRecordIds: [],
            clickBehavior: {
                type: "do-nothing"
            }
        }));
}
